import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { Col, Row } from "react-bootstrap";
import { Editor } from "components/shared/Editor";

import { editBlog, getBlogCategories, getSingleBlog } from "store/actions";
import { useNavigate, useParams } from "react-router-dom";
import server from "api/server";
import Loader from "components/shared/Loader";
import CreatableSelect from "react-select/creatable";
import { getId } from "helpers/functions";
import ImageUpload from "components/shared/ImageUpload";

const EditBlog = () => {
  const { locale } = useIntl();
  const dir = locale === "ar" ? "rtl" : "ltr";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const [selectedImage, setSelectedImage] = useState({
    preview: null,
    path: null,
  });

  const [description, setDescription] = useState({ ar: "", en: "" });
  const [tags, setTags] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm();

  const { singleBlog, loading } = useSelector((state) => state.blogs);
  const { blogCategories } = useSelector((state) => state.blogCategories);
  // const { languages } = useSelector((state) => state.languages);
  const { blogTags } = useSelector((state) => state.blogs) || {};

  useEffect(() => {
    dispatch(getBlogCategories());
    dispatch(getSingleBlog(id));
  }, [dispatch, id]);

  useEffect(() => {
    reset({
      description: singleBlog?.description,
      image: singleBlog?.image,
      tags: singleBlog?.tags,
      title: singleBlog?.title,
      category: getId(singleBlog?.category),
    });
    setSelectedImage({ path: singleBlog?.image });
    return () => reset({});
  }, [reset, singleBlog]);

  // const renderLanguagesSelectOptions = () => {
  //   return languages.map((language, index) => {
  //     return (
  //       <option value={getId(language)} key={index}>
  //         {language?.name}
  //       </option>
  //     );
  //   });
  // };

  const imageChange = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const formData = new FormData();
      formData.append("file", e.target.files[0]);
      const { data } = await server().post("/upload", formData);
      setSelectedImage({
        preview: e.target.files[0],
        path: data.data.files[0].path,
      });
    }
  };

  const removeSelectedImage = () => {
    setSelectedImage();
  };

  useEffect(() => {
    setTags(singleBlog?.tags);
  }, [singleBlog]);

  useEffect(() => {
    setDescription(singleBlog?.description);
  }, [singleBlog]);

  const renderTags = () => {
    return singleBlog?.tags?.map((tag) => {
      return {
        label: tag,
        value: tag,
      };
    });
  };

  const renderCategoriesOptions = blogCategories?.map((category) => (
    <option value={getId(category)}>{category?.title?.[locale]}</option>
  ));

  const onSubmit = (data) => {
    if (selectedImage && selectedImage.path) data["image"] = selectedImage.path;
    data.image = "uploads/" + data.image.split("/").pop();

    // if (selectedBanner && selectedBanner.path)
    // data["banner"] = selectedBanner.path;
    // data.banner = "uploads/" + data.banner.split("/").pop();

    data.description = description;
    data.tags = tags;

    delete data.relatedBlogs;

    dispatch(editBlog({ id, data, navigate }));
  };

  if (loading) return <Loader />;

  return (
    <>
      <div className="acc-form">
        <div className="card">
          <div className="card-head">
            <h4>
              <FormattedMessage id="editBlog" />
            </h4>
          </div>
          <div className="card-body form-group settings-tabs">
            <form onSubmit={handleSubmit(onSubmit)}>
              <Col xl={10} md={12}>
                <Row>
                  <Col lg={12}>
                    <div className="form-group required">
                      <h5>صورة المقال</h5>
                      <ImageUpload
                        selectedImage={selectedImage}
                        setSelectedImage={setSelectedImage}
                        accept="image/*, video/*"
                      />
                    </div>
                  </Col>
                  <Col xs={12}>
                    <div className="form-group required">
                      <h5>التصنيف</h5>
                      <div>
                        <select
                          className="form-control"
                          {...register("category", { required: true })}
                        >
                          <optionc value="">اختر التصنيف</optionc>
                          {renderCategoriesOptions}
                        </select>
                      </div>
                      <p className="error-hint">
                        {errors.category?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6} xs={12}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="title" /> (
                        <FormattedMessage id="arabic" />)
                      </h5>
                      <div>
                        <input
                          type="text"
                          className="form-control form-outline"
                          {...register("title.ar", {
                            required: true,
                          })}
                        />
                      </div>
                      <p className="error-hint">
                        {errors.title?.ar?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>
                  <Col lg={6} xs={12}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="title" /> (
                        <FormattedMessage id="english" />)
                      </h5>
                      <div>
                        <input
                          type="text"
                          className="form-control form-outline"
                          {...register("title.en", {
                            required: true,
                          })}
                        />
                      </div>
                      <p className="error-hint">
                        {errors.title?.en?.type === "required" && (
                          <FormattedMessage id="required" />
                        )}
                      </p>
                    </div>
                  </Col>

                  <Col lg={12} xs={12}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="description" /> (
                        <FormattedMessage id="arabic" />)
                      </h5>
                      <div>
                        <Controller
                          control={control}
                          name="description.ar"
                          render={({ field: { value } }) => (
                            <Editor
                              initialValue={description?.ar}
                              onChange={(e, editor) => {
                                const data = editor.getContent();
                                setDescription({
                                  ...description,
                                  ar: data,
                                });
                              }}
                              tinymceScriptSrc="https://cdn.tiny.cloud/1/qagffr3pkuv17a8on1afax661irst1hbr4e6tbv888sz91jc/tinymce/5-stable/tinymce.min.js"
                            />
                          )}
                        />
                        <p className="error-hint">
                          {errors.description?.ar?.type === "required" && (
                            <FormattedMessage id="required" />
                          )}
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={12} xs={12}>
                    <div className="form-group required">
                      <h5>
                        <FormattedMessage id="description" /> (
                        <FormattedMessage id="english" />)
                      </h5>
                      <div>
                        <Controller
                          control={control}
                          name="description.en"
                          render={({ field: { value } }) => (
                            <Editor
                              initialValue={description?.en}
                              onChange={(e, editor) => {
                                const data = editor.getContent();
                                setDescription({
                                  ...description,
                                  en: data,
                                });
                              }}
                              tinymceScriptSrc="https://cdn.tiny.cloud/1/qagffr3pkuv17a8on1afax661irst1hbr4e6tbv888sz91jc/tinymce/5-stable/tinymce.min.js"
                            />
                          )}
                        />
                        <p className="error-hint">
                          {errors.description?.en?.type === "required" && (
                            <FormattedMessage id="required" />
                          )}
                        </p>
                      </div>
                    </div>
                  </Col>
                  <Col lg={12}>
                    <div className="form-group">
                      <h5>
                        <FormattedMessage id="keywords" />
                      </h5>
                      <div>
                        <Controller
                          control={control}
                          name="tags"
                          render={({
                            field: { onChange, value, selected },
                          }) => (
                            <CreatableSelect
                              isMulti
                              isRtl={true}
                              className="basic-single creatable-select"
                              classNamePrefix="select"
                              options={blogTags.map((tag) => ({
                                value: tag,
                                label: tag,
                              }))}
                              defaultValue={
                                renderTags()?.filter((tag) => {
                                  return tag;
                                }) || []
                              }
                              onChange={(selected) => {
                                const newTags = selected.map(
                                  (tag) => tag.label
                                );

                                setTags(newTags);
                              }}
                            />
                          )}
                        />
                      </div>
                    </div>
                  </Col>
                </Row>
              </Col>

              <Col lg={4} xs={12}>
                <div className="form-group mt-3">
                  <button type="submit" className="btn btn-blue">
                    <FormattedMessage id="save" />
                  </button>
                </div>
              </Col>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditBlog;
